<template>
  <div>
    <Dialog
      v-model:visible="languagesData.show"
      modal
      :header="translate('choose your language', 'ucfirst')"
      :style="{ width: '40rem' }"
      :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
      :draggable="false"
    >
      <form>
        <InputText
          v-model="languagesData.value"
          @input="filterLanguages()"
          class="w-full mb-1.5 py-2 px-3 rounded-lg border border-color-iron"
        />

        <div class="w-full py-5">
          <ul class="flex flex-wrap gap-6">
            <template
              v-for="(langIso, langId) of languagesData.filtered" 
              :key="langId"
            >
              <li v-if="langIso[store.getters.getUserLang] != store.getters.getUserLangName && officialLanguages.includes(langIso['isoCode'])">
                <Button type="button" class="btn-register" @click="selectLanguage(langIso['llangue_id'])">
                  <img class="w-8 h-8 me-2" :src="getOnPublic(`data/langs/${langIso['isoCode']}.svg`)">
                  {{ ucfirst(langIso[store.getters.getUserLang] !== null ? langIso[store.getters.getUserLang] : langIso['en']) }}
                </Button>
              </li>
            </template>
          </ul>
        </div>
      </form>
    </Dialog>



    <footer id="application-footer" class="w-full footer">
      <p class="order-2 xl:order-none xl:text-sm">
        &copy; {{  env('APP_RELEASE_DATE') }} - {{ translate('all rights reserved to', 'ucfirst') }}
        <span class="font-bold text-oxford-blue">{{ env('APP_NAME') }}</span>
        | {{ translate('version', 'ucfirst') }} {{ [1,'1'].includes(env('APP_BETA')) ? 'BETA' : '' }} {{ env('APP_VERSION') }}
      </p>
      <ul
        class="flex flex-col md:flex-row xl:order-none space-y-1 md:space-y-0 md:space-x-6 lg:space-x-5"
      >
        <li><RouterLink to="/about">{{ translate('about us', 'capitalize') }}</RouterLink></li>
        <li v-if="[undefined, null, 0].includes(env('APP_BETA'))"><RouterLink to="/faq">{{ translate('frequent questions', 'capitalize') }}</RouterLink></li>
        <li><RouterLink to="/terms">{{ translate('terms of service', 'capitalize') }}</RouterLink></li>
        <li><RouterLink to="/privacy-policy">{{ translate('policy privacy', 'capitalize') }}</RouterLink></li>
        <li><button type="button" @click="openCookiePreferences">{{ cookieLabels.preferences }}</button></li>
        <li><RouterLink to="/status">{{ statusLinkLabel }}</RouterLink></li>
        <li><RouterLink to="/compatibility">{{ compatibilityLinkLabel }}</RouterLink></li>
      </ul>
      <button
        type="button"
        class="flex items-center font-bold space-x-2 mt-2 mb-4 md:my-0 lg:ml-auto xl:ml-0 cursor-pointer"
        @click="languagesData.show = true"
        aria-haspopup="dialog"
        :aria-expanded="languagesData.show"
      >
        <img class="w-8 h-8" :src="getOnPublic(`data/langs/${store.getters.getUserLang}.svg`)">
        <span>{{ ucfirst(store.getters.getUserLangName) }}</span>
        <i class="pi pi-chevron-up cursor-pointer"></i>
      </button>
    </footer>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import { RouterLink } from "vue-router";
import Dialog from "primevue/dialog";
import { openCookiePreferences } from '@/composables/cookieConsent';
import { cookieCopy } from '@/i18n/cookieCopy.mjs';
import InputText from "primevue/inputtext";
import { translate } from "@/composables/translate";
import { selectInterfaceLanguage } from '@/composables/interfaceLanguage';
import store from "@/store";
import Button from "primevue/button";
import languageService from "@/composables/languageData";
import { env } from "@/composables/config";
import { useToast } from "primevue/usetoast";
import { showToast } from "@/composables/toast";
import { ucfirst } from "@/composables/textUtils";
import { getOnPublic } from "@/composables/imageChecker";

const toast = useToast();
const cookieLabels = computed(() => cookieCopy[store.getters.getUserLang] || cookieCopy.en);
const statusLinkLabel = computed(() => ({
  pt: 'Status e suporte',
  es: 'Estado y soporte',
  en: 'Status and support',
}[store.getters.getUserLang] || 'Status and support'));
const compatibilityLinkLabel = computed(() => ({
  pt: 'Compatibilidade',
  es: 'Compatibilidad',
  en: 'Compatibility',
}[store.getters.getUserLang] || 'Compatibility'));

let languagesData = reactive({
  original: [],
  filtered: [],
  ids: {},
  enInfo: {},
  value: '',
  show: false
});

let officialLanguages = ref([]);
onMounted(function(){
  officialLanguages.value = languageService.getOfficialLanguagesIsoCodes();
  const langs = languageService.getAllLanguages();
  languagesData.original = langs;
  languagesData.filtered = langs;
  languagesData.ids = {};
  for(const [fallbackIso, language] of Object.entries(langs)){
    const isoCode = language.isoCode || fallbackIso;
    languagesData.ids[language.llangue_id] = {isoCode: isoCode, langs: language};
    if(isoCode == 'en')
      languagesData.enInfo = {isoCode: isoCode, langs: language};
  }
})

function filterLanguages(){
  languagesData.filtered = languagesData.original;
  if(languagesData.value == ''){
    return;
  }
  let arr = [];
  let filteredArray = Object.entries(languagesData.filtered);
  for(let lang of filteredArray){
    let translation = languagesData.filtered[lang[0]][store.getters.getUserLang] !== null ? languagesData.filtered[lang[0]][store.getters.getUserLang] : languagesData.filtered[lang[0]]['en'];
    if(translation.includes(languagesData.value))
      arr.push(languagesData.filtered[lang[0]]);
  }
  languagesData.filtered = arr;
}

async function selectLanguage(langId){
  let langData = languagesData.ids[langId];
  if(langData === undefined || langData.langs === undefined)
    langData = languagesData.enInfo;
  const langIso = langData.isoCode;
  try {
    await selectInterfaceLanguage(langIso);
  } catch (_) {
    showToast(toast, ({pt: 'Não foi possível carregar o idioma. Tente novamente.', es: 'No se pudo cargar el idioma. Inténtalo de nuevo.', en: 'Could not load the language. Please try again.'})[store.getters.getUserLang] || 'Could not load the language. Please try again.', 'error');
    return;
  }
  languagesData.show = false;
  languagesData.value = '';
  languagesData.filtered = languagesData.original;
  showToast(toast, translate('language changed', 'ucfirst'), 'success');
}

</script>

<style scoped></style>
